import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ShoppingBag, Loader2, Package, MapPin, Search, Truck } from "lucide-react";

const STATUS = {
  paid: { label: "Paid", cls: "bg-lime-100 text-lime-700 border-lime-200" },
  initiated: { label: "Awaiting payment", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  open: { label: "Open", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  expired: { label: "Expired", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const fmtDate = (s) => { try { return new Date(s).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => toast.error("Failed to load orders")).finally(() => setLoading(false));
  }, []);

  const paidCount = orders.filter((o) => o.payment_status === "paid").length;
  const revenue = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return orders;
    return orders.filter((o) => [o.customer_name, o.customer_email, o.delivery_address, o.user_email].filter(Boolean).some((v) => v.toLowerCase().includes(t)));
  }, [orders, q]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>;

  return (
    <div className="space-y-6 fade-up" data-testid="orders-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Parts Store</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2"><ShoppingBag className="h-8 w-8 text-lime-500" /> Orders</h1>
          <p className="text-slate-500 mt-1">Parts orders with customer & delivery details for fulfilment.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><p className="text-2xl font-bold font-heading text-slate-900" data-testid="orders-revenue">${revenue.toFixed(2)}</p><p className="text-xs text-slate-400">{paidCount} paid orders</p></div>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, email or address…" className="pl-9" data-testid="orders-search" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <ShoppingBag className="h-10 w-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-slate-500">{orders.length === 0 ? "No orders yet." : "No orders match your search."}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {filtered.map((o) => {
            const st = STATUS[o.payment_status] || STATUS[o.status] || { label: o.payment_status || "—", cls: "bg-slate-100 text-slate-500 border-slate-200" };
            return (
              <div key={o.session_id || o.id} onClick={() => navigate(`/orders/${o.id}`)} data-testid={`order-${o.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-slate-900">{o.customer_name || "Unnamed customer"}</span>
                      <Badge className={`border ${st.cls}`}>{st.label}</Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtDate(o.created_at)} · placed by {o.user_email || "—"}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-slate-500 mt-1.5">
                      {o.customer_email && <span>{o.customer_email}</span>}
                      {o.customer_phone && <span>{o.customer_phone}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xl font-bold font-heading text-slate-900">${(o.amount || 0).toFixed(2)} <span className="text-xs text-slate-400 font-normal">{(o.currency || "aud").toUpperCase()}</span></p>
                    {o.shipping_method && <p className="text-xs text-slate-400 flex items-center gap-1 justify-end mt-0.5"><Truck className="h-3 w-3" /> {o.shipping_method} (${(o.shipping_amount || 0).toFixed(2)})</p>}
                  </div>
                </div>

                {o.delivery_address && (
                  <p className="mt-3 text-sm text-slate-600 flex items-start gap-1.5"><MapPin className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" /> {o.delivery_address}</p>
                )}

                {o.items?.length > 0 && (
                  <div className="mt-3 border-t border-slate-100 pt-3 flex flex-wrap gap-2">
                    {o.items.map((it, i) => (
                      <span key={it.part_id || it.name || i} className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-1">
                        <Package className="h-3 w-3 text-slate-400" /> {it.name} <span className="text-slate-400">×{it.quantity}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
